import { useLazyQuery, useQuery } from "@apollo/client";
import { Box, Checkbox, FormControlLabel, Grid } from "@material-ui/core";
import PhoneField from "components/@phone-field";
import Dialog from "components/Dialog";
import { GeoAutoCompletion } from "components/GeoAutoCompletion";
import { TextField } from "components/TextField";
import { isValidPhoneNumberFormat } from "config/constants";
import { theme } from "config/theme";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { ALL_HUBS, GET_HUBS_BY_CITY, ReceiverByClientID } from "domains/Task/graphql/queries";
import HTTP_STATUS_CODES from "domains/Task/types/Column";
import { Address, Hub, JobItemsInput, Order, Receiver } from "generated/graphql";
import { useCountryCode } from "hooks/useCurrentUser";
import { formatIncompletePhoneNumber } from "libphonenumber-js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "styled-components/macro";
import Autocomplete from "ui/Autocomplete";
import { uuid, ValidReceiver } from "utils";
import { ReceiverList } from "./ReceiverList";
import { useReceiverStyles } from "./style";

const defaultAddress: Address = {
  address: "",
  place_id: "",
  additional_address: "",
};
const defaultReceiver: Receiver = {
  receiver_firstname: "",
  receiver_lastname: "",
  receiver_phone_number: "",
  receiver_entreprise: "",
  receiver_address: defaultAddress,
  receiver_specification_address: "",
};

export const formatReceiver = (receiver: Receiver) => ({
  receiver_email: receiver.receiver_email,
  receiver_lastname: receiver.receiver_lastname,
  receiver_firstname: receiver.receiver_firstname,
  receiver_entreprise: receiver?.receiver_entreprise ?? null,
  receiver_specification_address: receiver?.receiver_specification_address ?? null,
  receiver_phone_number: receiver.receiver_phone_number,
  receiver_address: {
    address: receiver?.receiver_address.address,
    place_id: receiver?.receiver_address?.place_id,
  },
});

export const alreadyExist = (receiver: Receiver, receivers: Receiver[]) => {
  return (
    receivers.findIndex(
      (item) =>
        formatIncompletePhoneNumber(item.receiver_phone_number || "") ===
          formatIncompletePhoneNumber(receiver.receiver_phone_number || "") &&
        item.receiver_lastname === receiver.receiver_lastname &&
        item.receiver_firstname === receiver.receiver_firstname
    ) !== -1
  );
};

export interface ReceiverProps {
  open: boolean;
  handleClose: () => void;
  clientId?: string;
  orders?: Order[];
  setOrders?: any;
  job?: JobItemsInput;
}

export default function NewReceiver({ open, orders, setOrders, job, handleClose, clientId }: ReceiverProps) {
  const [formValues, setFormValues] = useState(defaultReceiver);
  const [isFormSubmited, setFormSubmited] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [selectedReceivers, setSelectedReceivers] = useState([]);
  const [cityValue, setCityValue] = useState<string>(null);
  const [inputValueRelayPoint, setInputValueRelayPoint] = useState<Hub>(null);
  const [relays, setRelays] = useState([]);
  const [relayPointCity, setRelayPointCity] = useState([]);
  const classes = useReceiverStyles();
  const [queryReceiver, { data }] = useLazyQuery(ReceiverByClientID);
  const [getAllHubsByCity, { loading, data: hubsByCity }] = useLazyQuery(GET_HUBS_BY_CITY);
  const { data: relayData } = useQuery(ALL_HUBS);
  const [checked, setChecked] = useState(false);
  const { text } = useText();
  const countryCode = useCountryCode();
  const dispatchAlert = useAlertDispatch();
  const [relayPoints, setRelayPoints] = React.useState<any[]>([]);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };
  const handleCloseModal = () => {
    handleClose();
    setShowForm(false);
  };

  useEffect(() => {
    if (cityValue) {
      getAllHubsByCity({
        variables: {
          queries: {
            city: cityValue,
          },
        },
      });
    }
  }, [cityValue]);

  useEffect(() => {
    if (relayData) {
      const hubs = relayData?.allHubs;

      if (hubs?.code === HTTP_STATUS_CODES.OK) {
        const relayPointCity = hubs?.data?.map((hub: any) => hub?.location?.city);
        const filterCity = relayPointCity.filter((city: string) => city);
        setRelayPointCity(filterCity);
      }
    }
  }, [relayData]);

  useEffect(() => {
    if (hubsByCity) {
      const getHubs = hubsByCity?.getAllHubsByCity;
      if (getHubs?.code === HTTP_STATUS_CODES.OK) {
        setRelays(getHubs?.data);
      }
    }
  }, [hubsByCity]);

  const handleSelect = (event: any, name: string) => {
    setCityValue(name);
  };

  const handleOnInputRelaypointChange = (event: any, newInputValue: Hub) => {
    setInputValueRelayPoint(newInputValue);
  };

  const handleInputChange = useCallback(
    (event: any) => {
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value,
      });
    },
    [formValues]
  );
  React.useEffect(() => {
    if (clientId) {
      queryReceiver({
        variables: {
          params: { clientID: clientId },
        },
      });
    }
  }, [clientId, queryReceiver]);

  /**
   * add job on list or remove it if don't exist
   * @param row
   */
  const onSelectedReceiver = (rowIdRecaivers: string[]) => {
    setSelectedReceivers(rowIdRecaivers);
  };
  const SelectedReceiverViewCounter = React.useCallback(
    () => (
      <div className={classes.containerInfo}>
        <div className={classes.numbers}>{selectedReceivers.length ? selectedReceivers.length : 0}</div>
        <div className={classes.info}>{text.all.receivers_selected}</div>
      </div>
    ),
    [classes.containerInfo, classes.info, classes.numbers, selectedReceivers.length, text.all.receivers_selected]
  );

  const saveReceiver = (receivers: Receiver[]) => {
    const currentReceirvers = orders.map((item) => item.order_receiver);
    const newOrders: Order[] = [];
    receivers?.forEach((receiver) => {
      if (alreadyExist(receiver, currentReceirvers)) {
        dispatchAlert({
          severity: "warning",
          message: text.receiver.receiver_already_added,
        });
      } else if (showForm) {
        newOrders.push({
          _id: uuid(),
          order_receiver: receiver,
          order_parcels: [],
          relay: inputValueRelayPoint,
        });
        setInputValueRelayPoint(null);
        setChecked(false);
        setCityValue(null);
      } else {
        const relay = relayPoints?.find((item) => !!item[receiver?._id])?.[receiver?._id];
        newOrders.push({
          _id: uuid(),
          order_receiver: formatReceiver(receiver),
          order_parcels: [],
          ...(relay ? { relay } : {}),
        });
      }
      setOrders([...orders, ...newOrders]);
    });
  };

  const handleAddReceiver = () => {
    if (showForm) {
      setFormSubmited(true);

      if (!ValidReceiver.isValidSync(formValues)) return;

      saveReceiver([formatReceiver(formValues)]);
      setFormSubmited(false);
      setFormValues(defaultReceiver);
      setShowForm(false);
    } else if (selectedReceivers?.length > 0) {
      const receivers = data?.allReceiversByClient?.data.filter((item: Receiver) =>
        selectedReceivers?.includes(item?._id)
      );
      saveReceiver(receivers);
    }
    handleClose();
    setSelectedReceivers([]);
  };

  const isValidPhone = () => isValidPhoneNumberFormat(formValues?.receiver_phone_number);
  // eslint-disable-next-line no-undef

  const isValidRelay = useMemo(
    () => cityValue !== null && inputValueRelayPoint?.location?.address !== undefined,
    [checked, inputValueRelayPoint, cityValue]
  );

  const isFormValid = useMemo(
    () => (checked ? isValidRelay && ValidReceiver.isValidSync(formValues) : ValidReceiver.isValidSync(formValues)),
    [formValues, checked, isValidRelay]
  );
  const handleInputReceiverRelayPoint = (hubData: Hub, rowId: string) => {
    const currentReceirvers = orders.map((item) => item.order_receiver);
    const receiver = data?.allReceiversByClient?.data.find(({ _id }: any) => _id === rowId);
    if (!alreadyExist(receiver, currentReceirvers)) {
      setRelayPoints([...relayPoints, { [rowId]: hubData }]);
    }
  };

  return (
    <Dialog
      title={showForm ? `${text.all.add_receiver}` : `${text.all.select_receiver}`}
      open={open}
      confirmButtonTitle={showForm ? `${text.all.add}` : `${text.all.confirm}`}
      handleClose={handleCloseModal}
      handleConfirm={handleAddReceiver}
      maxWidth={showForm ? "md" : "lg"}
      key="add receiver dialog"
      showBottomInfo
      bottomInfoView={<SelectedReceiverViewCounter />}
      height="77vh"
      disableConfirmButton={showForm ? !isFormValid : selectedReceivers?.length === 0}
    >
      {showForm ? (
        <Grid container spacing={4} className={classes.showDesign}>
          <Grid item sm={6} xs={12}>
            <TextField
              fullWidth
              label={text.all.first_name}
              name="receiver_firstname"
              value={formValues.receiver_firstname}
              onChange={handleInputChange}
              error={isFormSubmited && formValues?.receiver_firstname?.length === 0}
              helperText={
                isFormSubmited && formValues?.receiver_firstname?.length === 0
                  ? `${text.all.this_field_is_required}`
                  : ""
              }
              required
              css={`
                ${theme.breakpoints.down("xs")} {
                  width: 86vw;
                }
              `}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <TextField
              css={`
                ${theme.breakpoints.down("xs")} {
                  width: 86vw;
                }
              `}
              fullWidth
              label={text.all.last_name}
              name="receiver_lastname"
              value={formValues.receiver_lastname}
              onChange={handleInputChange}
              error={isFormSubmited && formValues?.receiver_lastname?.length === 0}
              helperText={
                isFormSubmited && formValues?.receiver_lastname?.length === 0
                  ? `${text.all.this_field_is_required}`
                  : ""
              }
              required
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <TextField
              css={`
                ${theme.breakpoints.down("xs")} {
                  width: 86vw;
                }
              `}
              fullWidth
              label={text.all.entreprise}
              name="receiver_entreprise"
              value={formValues.receiver_entreprise}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <Box
              css={`
                ${theme.breakpoints.down("xs")} {
                  width: 86vw;
                }
              `}
            >
              <PhoneField
                countryCode={countryCode}
                fullWidth
                label={text.all.phone_number}
                name="receiver_phone_number"
                value={formValues.receiver_phone_number}
                onValueChange={(value) => {
                  setFormValues({
                    ...formValues,
                    receiver_phone_number: value,
                  });
                }}
                error={formValues.receiver_phone_number !== "" && !isValidPhone()}
                helperText={isFormSubmited && !isValidPhone() ? `${text.all.this_field_is_required}` : ""}
                required
                type="tel"
                placeholder={text.all.phone_number_placeholder}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <GeoAutoCompletion
              css={`
                ${theme.breakpoints.down("xs")} {
                  padding: 8px 33px 8px 321px;
                }
              `}
              onChange={(values) => setFormValues((prev) => ({ ...prev, receiver_address: values }))}
              address={formValues.receiver_address}
              error={
                (isFormSubmited && formValues?.receiver_address?.place_id === undefined) ||
                formValues?.receiver_address?.place_id === null ||
                formValues?.receiver_address?.address?.length === 0 ||
                formValues?.receiver_address?.address?.length === undefined
              }
              helperText={
                isFormSubmited &&
                (formValues?.receiver_address?.place_id === undefined ||
                  formValues?.receiver_address?.place_id === null ||
                  formValues?.receiver_address?.address?.length === 0 ||
                  formValues?.receiver_address?.address?.length === undefined)
                  ? `${text.all.this_field_is_required}`
                  : ""
              }
              required
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <Box
              css={`
                ${theme.breakpoints.down("xs")} {
                  margin-top: -15px;
                }
              `}
            >
              <TextField
                css={`
                  ${theme.breakpoints.down("xs")} {
                    width: 86vw;
                  }
                `}
                fullWidth
                label={text.all.specification}
                name="receiver_specification_address"
                value={formValues.receiver_specification_address}
                onChange={handleInputChange}
                helperText={
                  isFormSubmited && formValues?.receiver_specification_address?.length === 0
                    ? `${text.all.this_field_is_required}`
                    : ""
                }
                error={isFormSubmited && formValues?.receiver_specification_address?.length === 0}
                required
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              css={`
                ${theme.breakpoints.down("xs")} {
                  margin-top: -9px;
                }
              `}
            >
              <FormControlLabel
                css={`
                  ${theme.breakpoints.down("xs")} {
                    margin-right: -23px;
                  }
                `}
                control={
                  <Checkbox
                    checked={checked}
                    color="primary"
                    onChange={handleChange}
                    name="reception_at_relay"
                    inputProps={{ "aria-label": "primary checkbox" }}
                  />
                }
                label={text.all.reception_at_relay}
              />
            </Box>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Box
              css={`
                ${theme.breakpoints.down("xs")} {
                  margin-top: -20px;
                  margin-bottom: 12px;
                  width: 86vw;
                }
              `}
            >
              <Autocomplete
                disabled={!checked}
                options={relayPointCity}
                value={cityValue}
                onChange={handleSelect}
                label={text.all.city}
                name="city"
                getOptionLabel={(option: any) => option}
                renderInput={(params: any) => <TextField {...params} variant="outlined" />}
              />
            </Box>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Box
              css={`
                ${theme.breakpoints.down("xs")} {
                  margin-top: -9px;
                  margin-bottom: 15px;
                  width: 86vw;
                }
              `}
            >
              <Autocomplete
                disabled={!checked || loading || cityValue === null}
                options={relays}
                value={inputValueRelayPoint?.location?.address}
                onChange={handleOnInputRelaypointChange}
                label={text.all.relay_point}
                name="name"
                getOptionLabel={(option: any) => option?.name}
                renderInput={(params: any) => <TextField {...params} variant="outlined" />}
              />
            </Box>
          </Grid>
        </Grid>
      ) : (
        <ReceiverList
          showForm={setShowForm}
          receivers={data?.allReceiversByClient?.data}
          job={job}
          handleGetRowsChecked={onSelectedReceiver}
          handleHubSelect={(item) => setInputValueRelayPoint(item)}
          onAllRowChecked={(ids) => {
            if (ids.length === 0) {
              setSelectedReceivers([]);
            } else {
              setSelectedReceivers(data?.allReceiversByClient?.data);
            }
          }}
          handleGetReceiverHub={handleInputReceiverRelayPoint}
        />
      )}
    </Dialog>
  );
}
